import type { AccountMenuItem } from '@cwos/ui-admin';
import { hasPermission } from '../permissions.js';
import { visibleSettings } from '../settings/settingsNav.js';

/**
 * Account menu composition (doc 04 §1). The same permission rule as the rail:
 * gated entries are ABSENT when the permission is not held — never grayed-out.
 * Profile / View-as-worker / Sign out are authenticated-only, always present.
 */
export interface AccountMenuHandlers {
  onMyProfile: () => void;
  onViewAsWorker: () => void;
  onSettings: () => void;
  onNotifications: () => void;
  onNotificationPreferences: () => void;
  onIntegrations: () => void;
  onHelp: () => void;
  onSignOut: () => void;
}

export function buildAccountItems(
  permissions: readonly string[],
  handlers: AccountMenuHandlers,
): AccountMenuItem[] {
  const items: AccountMenuItem[] = [
    { key: 'profile', label: 'My profile', icon: 'people', onSelect: handlers.onMyProfile },
    {
      key: 'view-as-worker',
      label: 'View as worker',
      icon: 'handbook',
      onSelect: handlers.onViewAsWorker,
    },
  ];
  // Settings appears when the principal can read at least ONE settings section,
  // not when they hold one specific permission. Gating it on `org:manage` hid
  // the entry from people who could legitimately manage services, time off or
  // automations — they had the screens but no way to reach them from here.
  if (visibleSettings(permissions).length > 0) {
    items.push({
      key: 'settings',
      label: 'Settings',
      icon: 'setup',
      onSelect: handlers.onSettings,
    });
  }
  // The personal Notification Centre, not the organisation's SMS configuration.
  // This entry used to point at the org SMS screen and was gated on sms:manage,
  // so most people never saw it and the ones who did got a settings page rather
  // than their own notifications. Org SMS remains reachable under Settings.
  if (hasPermission(permissions, 'notification:read')) {
    items.push({
      key: 'notifications',
      label: 'Notifications',
      icon: 'bell',
      onSelect: handlers.onNotifications,
    });
  }
  if (hasPermission(permissions, 'notification-preferences:manage-own')) {
    items.push({
      key: 'notification-preferences',
      label: 'How I am notified',
      icon: 'setup',
      onSelect: handlers.onNotificationPreferences,
    });
  }
  if (hasPermission(permissions, 'integrations:read')) {
    items.push({
      key: 'integrations',
      label: 'Integrations',
      icon: 'refresh',
      onSelect: handlers.onIntegrations,
    });
  }
  // Always available: everyone can need help, regardless of what they may
  // configure.
  items.push({ key: 'help', label: 'Help & support', icon: 'handbook', onSelect: handlers.onHelp });
  items.push({ key: 'signout', label: 'Sign out', danger: true, onSelect: handlers.onSignOut });
  return items;
}
